'use client'

import { useState, useRef, useEffect } from 'react'
import { UtensilsCrossed, Coffee, Bike, Wine, Flame, Check, type LucideIcon } from 'lucide-react'
import {
  MockFloorPlan, MockDashboardStats, MockOrders, MockReservationBook, MockShiftSchedule,
} from './mockups'

// System-accurate panel mockups per segment — real Rezaro screens, no faked
// features. Index-matched conceptually to each segment id below.
const SEG_MOCK: Record<string, React.ReactNode> = {
  restaurant: <MockFloorPlan />,
  cafe: <MockDashboardStats />,
  lieferdienst: <MockOrders />,
  bar: <MockReservationBook />,
  shisha: <MockShiftSchedule />,
}

interface Bullet { t: string; s: string }
interface Segment {
  id: string
  label: string
  icon: LucideIcon
  headline: string
  subline: string
  bullets: Bullet[]
}

type Lang = 'de' | 'en' | 'tr'

/** Locale-independent segment shape: id + icon stay fixed across languages. */
const SEG_BASE: { id: string; icon: LucideIcon }[] = [
  { id: 'restaurant', icon: UtensilsCrossed },
  { id: 'cafe', icon: Coffee },
  { id: 'lieferdienst', icon: Bike },
  { id: 'bar', icon: Wine },
  { id: 'shisha', icon: Flame },
]

type SegText = Pick<Segment, 'label' | 'headline' | 'subline' | 'bullets'>

/**
 * Branche-Tabs — visitor can switch segment to see the value
 * proposition framed for their kind of business. Resmio doesn't
 * segment its product page at all.
 *
 * Text is locale-aware; German ('de') is the live default and must stay
 * byte-identical. Each locale array is index-aligned with SEG_BASE.
 */
const SEG_T: Record<Lang, SegText[]> = {
  de: [
    {
      label: 'Restaurant',
      headline: 'Volle Tische, weniger Telefon.',
      subline: 'So nutzen Restaurants in Deutschland Rezaro jeden Tag.',
      bullets: [
        { t: 'Reservierungen über Google, Website, Instagram und QR-Code', s: 'Ihre Gäste buchen dort, wo sie Sie finden.' },
        { t: 'Tischplan mit Aufenthaltsdauer pro Tischgröße', s: 'Volle Kontrolle über Ihre Auslastung in Echtzeit.' },
        { t: 'Anzahlung gegen No-Show ab gewählter Gruppengröße', s: 'Weniger No-Shows, mehr Planungssicherheit.' },
        { t: 'Wochenbericht zu Auslastung und Stoßzeiten', s: 'Daten verstehen, besser entscheiden, mehr Umsatz.' },
      ],
    },
    {
      label: 'Café',
      headline: 'Brunch-Plätze geordnet vergeben.',
      subline: 'So nutzen Cafés in Deutschland Rezaro jeden Tag.',
      bullets: [
        { t: 'Slot-Limit für Wochenend-Brunch (z. B. 90 Min. pro Tisch)', s: 'Mehr Gäste bedienen, ohne Qualität zu verlieren.' },
        { t: 'Wartelisten-Anmeldung bei voller Auslastung', s: 'Keine Umsatzverluste mehr durch verpasste Anfragen.' },
        { t: 'Digitale Karte mit Allergen-Hinweis nach LMIV', s: 'Sicherheit für Ihre Gäste, weniger Rückfragen für Ihr Team.' },
        { t: 'Gästenotizen — Latte ohne Zucker bleibt gespeichert', s: 'Persönlicher Service, der in Erinnerung bleibt.' },
      ],
    },
    {
      label: 'Lieferdienst',
      headline: 'Bestellungen ohne 30 % Provision.',
      subline: 'So nutzen Lieferdienste in Deutschland Rezaro jeden Tag.',
      bullets: [
        { t: 'Eigener Online-Shop unter Ihrem Namen, keine Plattform-Marge', s: 'Sie behalten 100 % Ihrer Gewinne.' },
        { t: 'Bestelleingang, Küchen-Bon und Fahrer-Disposition in einem Fenster', s: 'Alle Bestellungen im Blick, alles perfekt organisiert.' },
        { t: 'Lieferzonen mit Mindestbestellwert pro Postleitzahl', s: 'Automatische Prüfung und klare Regeln für jede Zone.' },
        { t: 'Stammkunden-Wiederkauf mit einem Klick', s: 'Ihre Kunden bestellen schneller, Sie verkaufen mehr.' },
      ],
    },
    {
      label: 'Bar & Lounge',
      headline: 'Reservierungen mit Mindestumsatz.',
      subline: 'So nutzen Bars & Lounges in Deutschland Rezaro jeden Tag.',
      bullets: [
        { t: 'Tisch- oder Loge-Reservierung mit Verzehrgutschein', s: 'Mindestumsatz direkt festlegen und Gäste informieren.' },
        { t: 'Anzahlung gegen ausbleibende Gäste am Freitag-/Samstagabend', s: 'Weniger No-Shows, mehr Sicherheit an Ihren stärksten Tagen.' },
        { t: 'Türsteher-Ansicht mit Gästeliste auf dem Handy', s: 'Zutrittskontrolle, Gästeinformationen und Notizen in einem Blick.' },
        { t: 'Privatraum-Buchung mit eigenem Formular und Zeitfenster', s: 'Perfekt für Events, Geburtstage und exklusive Buchungen.' },
      ],
    },
    {
      label: 'Shisha-Lounge',
      headline: 'Plätze fair, Wartezeiten kurz.',
      subline: 'So nutzen Shisha-Lounges in Deutschland Rezaro jeden Tag.',
      bullets: [
        { t: 'Reservierung mit Sitzplatz-Bindung — keine Doppelvergabe', s: 'Jeder Platz ist fest vergeben, Überbuchungen gehören der Vergangenheit an.' },
        { t: 'Mindestverzehr pro Person automatisch beim Buchen kommuniziert', s: 'Transparenz für Ihre Gäste, mehr Umsatz für Ihr Business.' },
        { t: 'Stammgast-Erkennung mit Lieblings-Tabaksorte', s: 'Persönlicher Service, der Gäste begeistert und bindet.' },
        { t: 'Schichtplan für Service und Tabakmischer in einer Ansicht', s: 'Dienstpläne, Aufgaben und Verfügbarkeiten zentral organisiert.' },
      ],
    },
  ],
  en: [
    {
      label: 'Restaurant',
      headline: 'Full tables, fewer phone calls.',
      subline: 'How restaurants across Germany use Rezaro every day.',
      bullets: [
        { t: 'Reservations via Google, website, Instagram and QR code', s: 'Your guests book where they already find you.' },
        { t: 'Floor plan with seating duration per table size', s: 'Full control over your occupancy in real time.' },
        { t: 'Deposit against no-shows from a chosen party size', s: 'Fewer no-shows, more planning certainty.' },
        { t: 'Weekly report on occupancy and peak times', s: 'Understand the data, decide better, earn more.' },
      ],
    },
    {
      label: 'Café',
      headline: 'Brunch seats, neatly assigned.',
      subline: 'How cafés across Germany use Rezaro every day.',
      bullets: [
        { t: 'Slot limit for weekend brunch (e.g. 90 min. per table)', s: 'Serve more guests without losing quality.' },
        { t: 'Waiting-list sign-up when fully booked', s: 'No more lost revenue from missed requests.' },
        { t: 'Digital menu with allergen info', s: 'Safety for your guests, fewer questions for your team.' },
        { t: 'Guest notes — latte without sugar stays saved', s: 'Personal service that stays memorable.' },
      ],
    },
    {
      label: 'Delivery',
      headline: 'Orders without 30% commission.',
      subline: 'How delivery services across Germany use Rezaro every day.',
      bullets: [
        { t: 'Your own online shop under your name, no platform margin', s: 'You keep 100% of your profits.' },
        { t: 'Order intake, kitchen ticket and driver dispatch in one window', s: 'Every order in view, perfectly organised.' },
        { t: 'Delivery zones with a minimum order value per postcode', s: 'Automatic checks and clear rules for every zone.' },
        { t: 'Reorders for regulars in a single click', s: 'Your customers order faster, you sell more.' },
      ],
    },
    {
      label: 'Bar & lounge',
      headline: 'Reservations with a minimum spend.',
      subline: 'How bars & lounges across Germany use Rezaro every day.',
      bullets: [
        { t: 'Table or booth reservation with a spend voucher', s: 'Set the minimum spend and inform guests upfront.' },
        { t: 'Deposit against no-shows on Friday and Saturday nights', s: 'Fewer no-shows, more certainty on your strongest days.' },
        { t: 'Door-staff view with the guest list on a phone', s: 'Access control, guest info and notes at a glance.' },
        { t: 'Private-room booking with its own form and time slot', s: 'Perfect for events, birthdays and exclusive bookings.' },
      ],
    },
    {
      label: 'Shisha lounge',
      headline: 'Fair seats, short waits.',
      subline: 'How shisha lounges across Germany use Rezaro every day.',
      bullets: [
        { t: 'Reservation tied to a specific seat — no double-booking', s: 'Every seat is firmly assigned; overbooking is history.' },
        { t: 'Minimum spend per person communicated automatically at booking', s: 'Transparency for your guests, more revenue for your business.' },
        { t: 'Regulars recognised with their favourite tobacco flavour', s: 'Personal service that delights and retains guests.' },
        { t: 'Shift plan for service and tobacco mixers in one view', s: 'Rosters, tasks and availability organised centrally.' },
      ],
    },
  ],
  tr: [
    {
      label: 'Restoran',
      headline: 'Dolu masalar, daha az telefon.',
      subline: 'Almanya genelinde restoranlar Rezaro\'yu her gün böyle kullanıyor.',
      bullets: [
        { t: 'Google, web sitesi, Instagram ve QR kod üzerinden rezervasyon', s: 'Misafirleriniz sizi buldukları yerden rezervasyon yapar.' },
        { t: 'Masa boyutuna göre oturma süreli masa planı', s: 'Doluluğunuz üzerinde gerçek zamanlı tam kontrol.' },
        { t: 'Seçilen grup büyüklüğünden itibaren no-show\'a karşı kapora', s: 'Daha az no-show, daha çok planlama güveni.' },
        { t: 'Doluluk ve yoğun saatler için haftalık rapor', s: 'Veriyi anla, daha iyi karar ver, daha çok kazan.' },
      ],
    },
    {
      label: 'Kafe',
      headline: 'Brunch yerleri, düzenli dağıtılır.',
      subline: 'Almanya genelinde kafeler Rezaro\'yu her gün böyle kullanıyor.',
      bullets: [
        { t: 'Hafta sonu brunch için slot limiti (ör. masa başına 90 dk.)', s: 'Kaliteden ödün vermeden daha çok misafir ağırla.' },
        { t: 'Doluluk halinde bekleme listesine kayıt', s: 'Kaçan taleplerden artık ciro kaybı yok.' },
        { t: 'Alerjen bilgili dijital menü', s: 'Misafire güven, ekibe daha az soru.' },
        { t: 'Misafir notları — şekersiz latte kayıtlı kalır', s: 'Akılda kalan kişisel servis.' },
      ],
    },
    {
      label: 'Paket servis',
      headline: '%30 komisyon olmadan siparişler.',
      subline: 'Almanya genelinde paket servisler Rezaro\'yu her gün böyle kullanıyor.',
      bullets: [
        { t: 'Kendi adınıza kendi online mağazanız, platform payı yok', s: 'Kazancınızın %100\'ü sizde kalır.' },
        { t: 'Sipariş girişi, mutfak fişi ve kurye sevkiyatı tek pencerede', s: 'Tüm siparişler tek bakışta, kusursuz düzen.' },
        { t: 'Posta koduna göre minimum sipariş tutarlı teslimat bölgeleri', s: 'Otomatik kontrol ve her bölge için net kurallar.' },
        { t: 'Daimi misafirler için tek tıkla yeniden sipariş', s: 'Müşteriniz daha hızlı sipariş verir, siz daha çok satarsınız.' },
      ],
    },
    {
      label: 'Bar & lounge',
      headline: 'Minimum harcamalı rezervasyonlar.',
      subline: 'Almanya genelinde bar & lounge\'lar Rezaro\'yu her gün böyle kullanıyor.',
      bullets: [
        { t: 'Harcama kuponlu masa veya loca rezervasyonu', s: 'Minimum harcamayı belirleyin ve misafire önceden bildirin.' },
        { t: 'Cuma/Cumartesi geceleri gelmeyen misafirlere karşı kapora', s: 'En yoğun günlerinizde daha az no-show, daha çok güven.' },
        { t: 'Telefonda misafir listeli kapı görevlisi görünümü', s: 'Giriş kontrolü, misafir bilgisi ve notlar tek ekranda.' },
        { t: 'Kendi formu ve zaman aralığı olan özel oda rezervasyonu', s: 'Etkinlik, doğum günü ve özel rezervasyonlar için ideal.' },
      ],
    },
    {
      label: 'Nargile salonu',
      headline: 'Adil yerler, kısa bekleme.',
      subline: 'Almanya genelinde nargile salonları Rezaro\'yu her gün böyle kullanıyor.',
      bullets: [
        { t: 'Belirli koltuğa bağlı rezervasyon — çift atama yok', s: 'Her yer kesin atanır; fazla rezervasyon tarihe karışır.' },
        { t: 'Kişi başı minimum harcama rezervasyonda otomatik bildirilir', s: 'Misafire şeffaflık, işletmeye daha çok ciro.' },
        { t: 'En sevdiği tütün çeşidiyle daimi misafir tanıma', s: 'Misafiri mutlu eden ve bağlayan kişisel servis.' },
        { t: 'Servis ve nargile hazırlayıcıları için tek görünümde vardiya planı', s: 'Vardiyalar, görevler ve uygunluklar merkezi olarak düzenli.' },
      ],
    },
  ],
}

function buildSegments(lang: Lang): Segment[] {
  return SEG_BASE.map((base, i) => ({ ...base, ...SEG_T[lang][i] }))
}

export function BrancheTabs({ lang = 'de' }: { lang?: Lang } = {}) {
  const SEGMENTS = buildSegments(lang)
  const [activeId, setActiveId] = useState<string>(SEGMENTS[0].id)
  const current = SEGMENTS.find(s => s.id === activeId) ?? SEGMENTS[0]
  const Icon = current.icon
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const listRef = useRef<HTMLDivElement>(null)
  const didMount = useRef(false)

  // Centre the active tab in the scrollable strip when it changes, so a
  // tapped tab (e.g. Lieferdienst) slides to the middle and the next
  // tabs (Bar, Shisha) become reachable. IMPORTANT: scroll only the tab
  // strip horizontally — never use scrollIntoView (it jumps the whole
  // PAGE to this section on load). Skip the first render entirely.
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return }
    const list = listRef.current
    const el = tabRefs.current[activeId]
    if (!list || !el) return
    const delta = el.getBoundingClientRect().left - list.getBoundingClientRect().left
    const target = list.scrollLeft + delta - (list.clientWidth - el.clientWidth) / 2
    list.scrollTo({ left: Math.max(0, target), behavior: 'smooth' })
  }, [activeId])

  return (
    <div className="rounded-2xl border border-pub-rule bg-pub-surface overflow-hidden">
      {/* Tab bar */}
      <div ref={listRef} role="tablist" aria-label="Branche auswählen"
        className="flex overflow-x-auto border-b border-pub-rule scrollbar-none">
        {SEGMENTS.map(seg => {
          const TabIcon = seg.icon
          const isActive = seg.id === activeId
          return (
            <button
              key={seg.id}
              ref={(el) => { tabRefs.current[seg.id] = el }}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls="branche-panel"
              onClick={() => setActiveId(seg.id)}
              className={[
                'relative flex-1 min-w-[108px] sm:min-w-[140px] flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 sm:py-4',
                'text-[12px] sm:text-[13px] font-semibold whitespace-nowrap cursor-pointer transition-colors',
                isActive
                  ? 'text-brand-700 bg-pub-surface'
                  : 'text-pub-muted hover:text-pub-ink hover:bg-pub-bg/60',
              ].join(' ')}
            >
              <TabIcon className="h-4 w-4" strokeWidth={1.7} />
              <span>{seg.label}</span>
              {isActive && (
                <span aria-hidden="true"
                  className="absolute left-0 right-0 bottom-0 h-[2px] bg-brand-600" />
              )}
            </button>
          )
        })}
      </div>

      {/* Active panel */}
      <div role="tabpanel" id="branche-panel" className="p-6 sm:p-8 lg:p-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: real Rezaro screen for this segment */}
          <div className="order-2 lg:order-1 min-w-0">
            {SEG_MOCK[current.id]}
          </div>

          {/* Right: value proposition */}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-brand-50 border border-brand-100 mb-5">
              <Icon className="h-5 w-5 text-brand-700" strokeWidth={1.7} />
            </div>
            <h3 className="font-semibold leading-tight tracking-tight text-pub-ink"
              style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2rem)' }}>
              <span className="display-serif gradient-text">{current.headline}</span>
            </h3>
            <ul className="mt-6 space-y-4">
              {current.bullets.map(b => (
                <li key={b.t} className="flex gap-3 items-start">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-brand-200 bg-brand-50 text-brand-700">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold text-pub-ink leading-snug">{b.t}</span>
                    <span className="block text-[13.5px] text-pub-muted leading-relaxed mt-0.5">{b.s}</span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[13px] text-pub-muted">{current.subline}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
